import { Camera, BASE_CELL_SIZE } from './camera';
import { GameEngine } from '../game/state/gameEngine';
import { isMine } from '../game/core/mineGenerator';
import { getAdjacentMineCount } from '../game/mechanics/adjacent';
import { getUnlockCost } from '../game/mechanics/penalty';

export interface RenderTheme {
  isDark: boolean;
  bg: string;
  gridLine: string;
  boxBorder: string;
  cellHiddenBg: string;
  cellHiddenBorder: string;
  cellRevealedBg: string;
  cellRevealedBorder: string;
  flagBg: string;
  flagColor: string;
  completedBoxBg: string;
  completedBoxBorder: string;
  bustedBoxBg: string;
  bustedBoxBorder: string;
  textColor: string;
}

export const DARK_THEME: RenderTheme = {
  isDark: true,
  bg: '#000000',
  gridLine: 'rgba(255, 255, 255, 0.06)',
  boxBorder: 'rgba(255, 255, 255, 0.25)',
  cellHiddenBg: '#18181b',
  cellHiddenBorder: '#27272a',
  cellRevealedBg: '#09090b',
  cellRevealedBorder: '#18181b',
  flagBg: 'rgba(239, 68, 68, 0.15)',
  flagColor: '#ef4444',
  completedBoxBg: 'rgba(34, 197, 94, 0.04)',
  completedBoxBorder: '#22c55e',
  bustedBoxBg: 'rgba(239, 68, 68, 0.12)',
  bustedBoxBorder: '#ef4444',
  textColor: '#f4f4f5',
};

export const LIGHT_THEME: RenderTheme = {
  isDark: false,
  bg: '#f5f5f0',
  gridLine: 'rgba(0, 0, 0, 0.08)',
  boxBorder: 'rgba(0, 0, 0, 0.3)',
  cellHiddenBg: '#e4e4e7',
  cellHiddenBorder: '#d4d4d8',
  cellRevealedBg: '#ffffff',
  cellRevealedBorder: '#e4e4e7',
  flagBg: 'rgba(239, 68, 68, 0.15)',
  flagColor: '#dc2626',
  completedBoxBg: 'rgba(34, 197, 94, 0.05)',
  completedBoxBorder: '#16a34a',
  bustedBoxBg: 'rgba(239, 68, 68, 0.15)',
  bustedBoxBorder: '#dc2626',
  textColor: '#18181b',
};

const NUMBER_COLORS: Record<number, string> = {
  1: '#3b82f6', // Blue
  2: '#22c55e', // Green
  3: '#ef4444', // Red
  4: '#a855f7', // Purple
  5: '#b91c1c', // Maroon
  6: '#14b8a6', // Teal
  7: '#eab308', // Yellow
  8: '#64748b', // Slate
};

export function renderBoard(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  camera: Camera,
  engine: GameEngine,
  theme: RenderTheme
): void {
  const dpr = window.devicePixelRatio || 1;
  const canvasWidth = width * dpr;
  const canvasHeight = height * dpr;

  if (ctx.canvas.width !== canvasWidth || ctx.canvas.height !== canvasHeight) {
    ctx.canvas.width = canvasWidth;
    ctx.canvas.height = canvasHeight;
  }

  ctx.save();
  ctx.scale(dpr, dpr);

  // Clear background
  ctx.fillStyle = theme.bg;
  ctx.fillRect(0, 0, width, height);

  const bounds = camera.getViewportBounds(width, height);
  const cellSize = BASE_CELL_SIZE * camera.zoom;

  // Render Cells
  for (let cy = bounds.minCellY; cy <= bounds.maxCellY; cy++) {
    for (let cx = bounds.minCellX; cx <= bounds.maxCellX; cx++) {
      const { screenX, screenY } = camera.worldToScreen(cx, cy, width, height);

      // Skip cells outside canvas screen bounds
      if (
        screenX + cellSize < 0 ||
        screenX > width ||
        screenY + cellSize < 0 ||
        screenY > height
      ) {
        continue;
      }

      const state = engine.getCellState(cx, cy);
      const isMineCell = isMine(engine.seed, cx, cy, engine.mineProbability);

      // Cell container background
      if (state === 'REVEALED') {
        ctx.fillStyle = theme.cellRevealedBg;
        ctx.fillRect(screenX, screenY, cellSize, cellSize);
        ctx.strokeStyle = theme.cellRevealedBorder;
        ctx.lineWidth = 1;
        ctx.strokeRect(screenX, screenY, cellSize, cellSize);

        if (isMineCell) {
          // Mine icon
          ctx.fillStyle = '#ef4444';
          ctx.beginPath();
          ctx.arc(
            screenX + cellSize / 2,
            screenY + cellSize / 2,
            cellSize * 0.28,
            0,
            Math.PI * 2
          );
          ctx.fill();
        } else {
          const adj = getAdjacentMineCount(engine.seed, cx, cy, engine.mineProbability);
          if (adj > 0) {
            ctx.fillStyle = NUMBER_COLORS[adj] || theme.textColor;
            ctx.font = `700 ${Math.round(cellSize * 0.55)}px "JetBrains Mono", monospace`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(adj.toString(), screenX + cellSize / 2, screenY + cellSize / 2);
          }
        }
      } else {
        // HIDDEN or FLAGGED
        ctx.fillStyle = theme.cellHiddenBg;
        ctx.fillRect(screenX, screenY, cellSize, cellSize);
        ctx.strokeStyle = theme.cellHiddenBorder;
        ctx.lineWidth = 1;
        ctx.strokeRect(screenX, screenY, cellSize, cellSize);

        if (state === 'FLAGGED') {
          ctx.fillStyle = theme.flagBg;
          ctx.fillRect(screenX, screenY, cellSize, cellSize);

          ctx.font = `${Math.round(cellSize * 0.5)}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('🚩', screenX + cellSize / 2, screenY + cellSize / 2);
        }
      }
    }
  }

  // Render 10x10 Boxes Grid & Statuses
  for (let by = bounds.minBoxY; by <= bounds.maxBoxY; by++) {
    for (let bx = bounds.minBoxX; bx <= bounds.maxBoxX; bx++) {
      const boxStatus = engine.getBoxStatus(bx, by);
      const startCellX = bx * 10;
      const startCellY = by * 10;

      const { screenX: boxScreenX, screenY: boxScreenY } = camera.worldToScreen(
        startCellX,
        startCellY,
        width,
        height
      );
      const boxSizePx = cellSize * 10;

      // Draw box border
      ctx.strokeStyle = theme.boxBorder;
      ctx.lineWidth = Math.max(2, 2.5 * camera.zoom);
      ctx.strokeRect(boxScreenX, boxScreenY, boxSizePx, boxSizePx);

      if (boxStatus === 'COMPLETED') {
        // Faint green layer tint on top
        ctx.fillStyle = theme.completedBoxBg;
        ctx.fillRect(boxScreenX, boxScreenY, boxSizePx, boxSizePx);
        // Green boundary stroke
        ctx.strokeStyle = theme.completedBoxBorder;
        ctx.lineWidth = Math.max(3, 3 * camera.zoom);
        ctx.strokeRect(boxScreenX, boxScreenY, boxSizePx, boxSizePx);
        // (Removed checkmark and text overlay to keep all numbers 100% visible)
      } else if (boxStatus === 'BUSTED') {
        ctx.fillStyle = theme.bustedBoxBg;
        ctx.fillRect(boxScreenX, boxScreenY, boxSizePx, boxSizePx);
        ctx.strokeStyle = theme.bustedBoxBorder;
        ctx.lineWidth = Math.max(3, 3.5 * camera.zoom);
        ctx.strokeRect(boxScreenX, boxScreenY, boxSizePx, boxSizePx);

        // Busted Overlay Text
        const cost = getUnlockCost(engine.failureCount);
        ctx.fillStyle = '#ef4444';
        ctx.font = `800 ${Math.max(14, 18 * camera.zoom)}px "Outfit", sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(
          '💥 BUSTED / LOCKED',
          boxScreenX + boxSizePx / 2,
          boxScreenY + boxSizePx / 2 - 12 * camera.zoom
        );

        ctx.fillStyle = theme.textColor;
        ctx.font = `600 ${Math.max(11, 14 * camera.zoom)}px "Outfit", sans-serif`;
        ctx.fillText(
          `Tap to unlock (⭐ ${cost})`,
          boxScreenX + boxSizePx / 2,
          boxScreenY + boxSizePx / 2 + 12 * camera.zoom
        );
      }
    }
  }

  ctx.restore();
}
